import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ThunderboltOutlined, LoadingOutlined } from '@ant-design/icons';
import Toast from '../../helpers/Toast';
import { suggestDescription } from '../../services/AiService';
import styles from './AiAssist.module.scss';

/**
 * Bouton "Proposer une description (IA)" + carte de proposition.
 * À placer juste sous le champ description. À l'acceptation, appelle onApply.
 *
 * @param {'tache'|'affaire'} type
 * @param {() => string} getNom        Retourne l'intitulé courant du formulaire
 * @param {() => string} getCurrent    Retourne la description actuelle (optionnel)
 * @param {(text: string) => void} onApply
 */
export default function AiDescriptionSuggest({ type, getNom, getCurrent, onApply }) {
    const [loading, setLoading]   = useState(false);
    const [proposal, setProposal] = useState(null);
    const abortRef = useRef(null);

    const run = async () => {
        const nom = (getNom?.() ?? '').trim();
        if (!nom) {
            Toast.info("Saisissez d'abord un intitulé.");
            return;
        }

        setLoading(true);
        setProposal(null);
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const text = await suggestDescription(type, nom, getCurrent?.() ?? null, controller.signal);
            if (text) setProposal(text);
            else Toast.error('Suggestion indisponible. Réessayez.');
        } catch (err) {
            if (err.name === 'CanceledError' || err.name === 'AbortError') return;
            Toast.error(err?.response?.data?.error ?? 'Suggestion indisponible. Réessayez.');
        } finally {
            setLoading(false);
        }
    };

    const apply = () => {
        if (proposal) onApply(proposal);
        setProposal(null);
    };

    return (
        <div className={styles.descWrap}>
            {!loading && (
                <button type="button" className={styles.descTriggerBtn} onClick={run}>
                    <ThunderboltOutlined />
                    {proposal ? 'Régénérer une description' : 'Proposer une description (IA)'}
                </button>
            )}

            {loading && (
                <div className={styles.descLoading}>
                    <LoadingOutlined /> {"Rédaction d'une proposition…"}
                </div>
            )}

            {proposal && !loading && (
                <div className={styles.descCard}>
                    <div className={styles.descCardLabel}>
                        <ThunderboltOutlined /> Proposition
                    </div>
                    <div className={styles.descCardText}>{proposal}</div>
                    <div className={styles.descCardActions}>
                        <button type="button" className={styles.primaryAction} onClick={apply}>
                            Utiliser ce texte
                        </button>
                        <button type="button" className={styles.ghostAction} onClick={() => setProposal(null)}>
                            Annuler
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

AiDescriptionSuggest.propTypes = {
    type: PropTypes.oneOf(['tache', 'affaire']).isRequired,
    getNom: PropTypes.func.isRequired,
    getCurrent: PropTypes.func,
    onApply: PropTypes.func.isRequired,
};
